// hooks/useSharedSession.ts - Enhanced with sharingHelpers
import { useState, useRef } from 'react';
import { loadSharedSession } from '../services/sharedSessionService';
import { VideoState, SegmentState, normalizeVideoViewCrop } from '../types';
import { 
  calculateDaysRemaining,
  calculateViewsRemaining,
  formatTimeRemaining,
  getShareStatus 
} from '../../utils/sharingHelpers';
import { stripShareSessionParams } from '../../utils/shareSessionUrl';
import { migrateLegacyTransitionType } from '../transitionCompat';
import { getFirstComboId, normalizeCombos, resolveSegmentOverlapSeconds } from '../utils';

export const useSharedSession = () => {
  const [isLoadingSharedSession, setIsLoadingSharedSession] = useState(false);
  const [sharedSessionError, setSharedSessionError] = useState<string | null>(null);
  const shareLoadAttemptedRef = useRef(false);

  /**
   * Load shared session from URL parameter
   * ENHANCED: Now uses sharingHelpers for better messaging
   */
  const loadSharedSessionFromUrl = async (
    setVideoState: (updates: Partial<VideoState>) => void,
    setSegmentState: (updates: Partial<SegmentState>) => void,
    setUIState: (updates: { isDemo: boolean }) => void,
    setOriginalVideoFile: (file: File | null) => void,
    providedShareId?: string
  ) => {
    const shareId = providedShareId?.trim() || '';
    
    if (!shareId) return;

    try {
      setIsLoadingSharedSession(true);
      setSharedSessionError(null);
      
      console.log('🟡 Loading shared session:', shareId);
      
      // Load shared session data (with Phase 2 validation)
      const sharedSession = await loadSharedSession(shareId);
      
      console.log('🟢 Shared session loaded:', sharedSession.title);
      
      // Set loading state for video
      setVideoState({ 
        isLoading: true, 
        loadingSource: 'firebase',
        videoSrc: null
      });
      
      // Load the session data
      const transitionMigration = migrateLegacyTransitionType(sharedSession.settings.transitionType);

      setVideoState({
        videoSrc: sharedSession.videoUrl,
        videoFileName: sharedSession.title,
        playbackRate: sharedSession.settings.defaultSpeed || 1.0,
        isMirrored: sharedSession.settings.defaultMirror ?? true,
        userMutePreference: sharedSession.settings.defaultMuted ?? false,
        isPlaying: false,
        currentTime: 0,
        isLoading: true,
        viewCrop: normalizeVideoViewCrop(sharedSession.settings.viewCrop)
      });
      
      const normalizedSegments = normalizeCombos(sharedSession.segments);
      const loadedSegmentOverlapSeconds = resolveSegmentOverlapSeconds(sharedSession.settings, normalizedSegments);

      setSegmentState({
        segments: normalizedSegments,
        activeSegment: null,
        activeComboId: getFirstComboId(normalizedSegments),
        isMarkingSegment: false,
        currentSegmentStart: null,
        isLooping: sharedSession.settings.autoLoop || false,
        transitionType: transitionMigration.transitionType,
        segmentOverlap: loadedSegmentOverlapSeconds
      });
      
      setUIState({ isDemo: false });
      setOriginalVideoFile(null);
      
      // ENHANCED: Use helper functions for better info display
      const expiresAt = sharedSession.expiresAt instanceof Date 
        ? sharedSession.expiresAt 
        : new Date(sharedSession.expiresAt);
      
      const daysRemaining = calculateDaysRemaining(expiresAt);
      const timeFormatted = formatTimeRemaining(expiresAt);
      const viewsRemaining = calculateViewsRemaining(
        sharedSession.accessCount, 
        sharedSession.maxViews
      );
      const status = getShareStatus(sharedSession);
      
      console.log(`🟢 Shared session loaded!`);
      console.log(`   Time remaining: ${timeFormatted}`);
      console.log(`   Views remaining: ${viewsRemaining}/${sharedSession.maxViews}`);
      console.log(`   Status: ${status}`);
      
      // Clean up URL
      window.history.replaceState(
        {},
        document.title,
        stripShareSessionParams(window.location.href)
      );
      
      setIsLoadingSharedSession(false);
      
      // ENHANCED: Better notification with limits info
      setTimeout(() => {
        const segmentText = sharedSession.segments.length === 1 
          ? '1 segment' 
          : `${sharedSession.segments.length} segments`;
        
        // Show warning if near limits
        let warningText = '';
        if (viewsRemaining <= 5) {
          warningText = `\n⚠️ Only ${viewsRemaining} views remaining on this share!`;
        } else if (daysRemaining <= 1) {
          warningText = `\n⚠️ This share expires ${timeFormatted}!`;
        }
        
        alert(
          `📺 Viewing: "${sharedSession.title}"\n\n` +
          `${segmentText} • ${viewsRemaining} views left • ${timeFormatted} remaining` +
          warningText
        );
      }, 500);
      
    } catch (error) {
      console.error('🔴 Failed to load shared session:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setSharedSessionError(errorMessage);
      setIsLoadingSharedSession(false);
      
      // Clean up URL
      window.history.replaceState(
        {},
        document.title,
        stripShareSessionParams(window.location.href)
      );
      
      // ENHANCED: Better error messages based on error type
      let userMessage = 'Failed to load shared session:\n\n';
      
      if (errorMessage.includes('expired')) {
        userMessage += '⏰ This share link has expired (7 days maximum).\n\n' +
                      'Ask the owner to create a new share link.';
      } else if (errorMessage.includes('view limit')) {
        userMessage += '👀 This share has reached its view limit (30 views maximum).\n\n' +
                      'Ask the owner to create a new share link.';
      } else if (errorMessage.includes('not found')) {
        userMessage += '🔍 Share link not found.\n\n' +
                      'The link may have been deleted or is incorrect.';
      } else if (errorMessage.includes('revoked')) {
        userMessage += '🚫 This share link has been revoked by the owner.';
      } else {
        userMessage += errorMessage + '\n\n' +
                      'The link may have expired or been deleted.';
      }
      
      alert(userMessage);
    }
  };

  /**
   * Mark that share load was attempted
   */
  const markShareLoadAttempted = () => {
    shareLoadAttemptedRef.current = true;
  };

  /**
   * Check if share load was attempted
   */
  const hasShareLoadAttempted = () => {
    return shareLoadAttemptedRef.current;
  };

  return {
    isLoadingSharedSession,
    sharedSessionError,
    loadSharedSessionFromUrl,
    markShareLoadAttempted,
    hasShareLoadAttempted
  };
};
